import React, { useEffect, useMemo, useState } from 'react';
import { PlayCircle, Clock3, GraduationCap, Search, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CamaraStreamVideo, getCamaraStreamVideos } from '../services/camaraStreamService.ts';

const categories = ['Todos', 'Ventas', 'Marketing', 'Gestión', 'IA'] as const;

export default function CamaraStream() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<(typeof categories)[number]>('Todos');
  const [videos, setVideos] = useState<CamaraStreamVideo[]>([]);

  useEffect(() => {
    getCamaraStreamVideos().then((items) => setVideos((items || []).filter((v) => v.activo !== false)));
  }, []);

  const featured = videos[0];

  const filtered = useMemo(() => {
    return videos.filter((v) => {
      const byCategory = category === 'Todos' || v.category === category;
      const byText = !q.trim() || `${v.title} ${v.instructor} ${v.category}`.toLowerCase().includes(q.toLowerCase());
      return byCategory && byText;
    });
  }, [q, category, videos]);

  return (
    <div className="min-h-screen bg-[#f3f8ff] p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
                <Home className="w-4 h-4 mr-2" />
                Inicio
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <PlayCircle className="w-4 h-4 text-gray-400" />
                <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Cámara Stream</span>
              </div>
            </li>
          </ol>
        </nav>

        <section className="rounded-2xl bg-gradient-to-r from-blue-700 to-sky-600 p-5 text-white shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-sky-100">Nuevo</p>
              <h1 className="text-3xl font-extrabold">Cámara Stream</h1>
              <p className="mt-1 text-sm text-sky-100">Capacitaciones tipo YouTube para socios: ventas, marketing, gestión e IA.</p>
            </div>
            <div className="rounded-xl bg-white/15 px-4 py-3 text-sm backdrop-blur">
              <p className="font-semibold">{videos.length} videos</p>
              <p className="text-sky-100">Actualización semanal</p>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Explorar contenido</p>
          <div className="mt-2 flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex w-full items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
              <Search className="h-4 w-4 text-sky-700" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar contenido: fotos de producto, ventas online, publicaciones..." className="w-full bg-transparent text-sm outline-none" />
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)} className={`rounded-full px-3 py-1 text-xs font-semibold ${category === c ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-700'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <article className="overflow-hidden rounded-2xl border border-sky-100 bg-white shadow-sm">
            <div className="aspect-video w-full bg-slate-100">
              <iframe
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${featured?.youtube_id || ''}`}
                title={featured?.title || 'Video destacado'}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4">
              <h2 className="text-xl font-bold text-slate-900">{featured?.title || 'Sin video destacado'}</h2>
              <p className="mt-1 text-sm text-slate-600">Instructor: {featured?.instructor || '—'}</p>
            </div>
          </article>

          <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
              <p className="font-semibold">Ruta recomendada</p>
              <p className="mt-1">Foto de producto → Publicación → Ventas online</p>
            </div>
            <div className="mt-3 rounded-xl bg-blue-50 p-3 text-sm text-blue-800">
              Mostrando <b>{filtered.length}</b> video(s) {category !== 'Todos' ? `en ${category}` : ''}.
            </div>
          </aside>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((v) => (
            <article key={v.video_id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-3 aspect-video overflow-hidden rounded-xl bg-slate-100">
                <iframe
                  className="h-full w-full"
                  src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}`}
                  title={v.title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <h3 className="line-clamp-2 text-sm font-bold text-slate-900">{v.title}</h3>
              <p className="mt-1 text-xs text-slate-600">{v.instructor}</p>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="inline-flex items-center gap-1 text-slate-600"><Clock3 className="h-3.5 w-3.5" /> {v.duration}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700"><GraduationCap className="h-3.5 w-3.5" /> {v.level}</span>
              </div>
              <button className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">
                <PlayCircle className="h-4 w-4" /> Ver capacitación
              </button>
            </article>
          ))}
        </section>
      </div>
    </div>
  );
}
